// Global auth variables
let graphToken = null;
let authCode = null;

// Context variables
let isCloud = true;
let exchangeHost = "";
let secopsMail = "";
let itemChangeKey = "";
const environment = process.env.APP_ENV;
let forMSGraphAccess = true;
if (environment === 'development') {
  console.log("Development environment");
  forMSGraphAccess = false;
}

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    console.log("Starting Office.onReady");

    // 1. DETECT ENVIRONMENT (Cloud vs. On-Premise)
    const ewsUrl = Office.context.mailbox.ewsUrl;

    if (ewsUrl && !ewsUrl.toLowerCase().includes("outlook.office365.com")) {
      isCloud = false;
      try {
        exchangeHost = new URL(ewsUrl).hostname;
      } catch (e) {
        exchangeHost = ewsUrl;
      }
      console.log("Exchange On-Premise detected. Host: " + exchangeHost);
    } else {
      console.log("Microsoft 365 (Cloud) detected.");
    }

    // 2. AUTH ROUTING
    if (!isCloud) {
      // ON-PREMISE: No separate token needed — makeEwsRequestAsync uses the
      // ambient Exchange session. Proceed straight to init.
      setInitialContent();
    } else {
      const hostName = Office.context.mailbox.diagnostics.hostName;
      if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
        console.log("Mobile detected");
        signIn();
      } else {
        console.log("Desktop or web detected");
        OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true, forMSGraphAccess: forMSGraphAccess })
          .then((token) => {
            graphToken = token;
            console.log("graphToken successfully obtained.");
            setInitialContent();
          })
          .catch((error) => {
            console.error("Error getting access token, retrying with Dialog API: ", error + " " + error.code);
            signIn();
          });
      }
    }

    // Register UI button handlers
    ["move-to-deleted", "report-and-delete"].forEach(id => {
      const button = document.getElementById(id);
      if (button) button.addEventListener("click", () => handleButtonClick(button));
    });
  }
});

let dialog = null;

function signIn() {
  Office.context.ui.displayDialogAsync(process.env.DIALOG_URL,
    { height: 60, width: 30, displayInIframe: false },
    function (result) {
      dialog = result.value;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, processAuthMessage);
    }
  );
}

function processAuthMessage(arg) {
  const message = JSON.parse(arg.message);
  if (message.code) {
    console.log("Authorization code successfully obtained: " + message.code);
    authCode = message.code;
    setInitialContent();
  } else if (message.error) {
    console.error("Authentication error: ", message.error);
    toggleDisplay("app-body-api-not-accessible", "flex");
  }
  if (dialog) dialog.close();
}

function toggleDisplay(elementId, displayStyle) {
  const element = document.getElementById(elementId);
  if (element) {
    element.style.display = displayStyle;
  }
}

// Tracks the button currently showing a loading spinner so we can restore it on error.
let activeButton: HTMLElement | null = null;

function resetActiveButton() {
  if (!activeButton) return;
  if (activeButton.dataset.originalHtml !== undefined) {
    activeButton.innerHTML = activeButton.dataset.originalHtml;
  }
  activeButton.removeAttribute('disabled');
  activeButton.classList.remove('disabled');
  activeButton = null;
}

function showError(containerId: string, detailId?: string, message?: string) {
  if (detailId && message) {
    const detail = document.getElementById(detailId);
    if (detail) detail.textContent = message;
  }
  toggleDisplay(containerId, "flex");
  resetActiveButton();
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function handleButtonClick(button) {
  activeButton = button;
  if (button.dataset.originalHtml === undefined) {
    button.dataset.originalHtml = button.innerHTML;
  }
  button.setAttribute('disabled', 'disabled');
  button.innerHTML = '<div class="loader"></div>';
  button.classList.add('disabled');

  const itemId = Office.context.mailbox.item.itemId;

  if (button.id === "move-to-deleted") {
    if (!isCloud) {
      ewsMoveToDeletedItems(itemId)
        .then(() => Office.context.ui.closeContainer())
        .catch((err) => {
          console.error("EWS simulation delete error", err);
          showError("move-email-error", "move-email-error-detail", errorMessage(err));
        });
    } else {
      callBackendApi('/addin/emaildeletion')
        .then((res) => {
          if (!res || !res.ok) {
            showError("move-email-error", "move-email-error-detail", `Request failed (${res ? res.status : "no response"})`);
          } else {
            Office.context.ui.closeContainer();
          }
        })
        .catch((err) => {
          console.error("Cloud simulation delete error", err);
          showError("move-email-error", "move-email-error-detail", errorMessage(err));
        });
    }
  } else if (button.id === "report-and-delete") {
    if (!isCloud) {
      ewsForwardEmail(itemId, secopsMail, itemChangeKey)
        .then(() => ewsMoveToDeletedItems(itemId))
        .then(() => Office.context.ui.closeContainer())
        .catch((err) => {
          console.error("EWS forward/delete error", err);
          showError("report-email-error", "report-email-error-detail", errorMessage(err));
        });
    } else {
      callBackendApi('/addin/emailforwarding')
        .then((res) => {
          if (!res || !res.ok) throw new Error(`Forwarding failed (${res ? res.status : "no response"})`);
          return callBackendApi('/addin/emaildeletion');
        })
        .then((res) => {
          if (res && !res.ok) console.warn("Deletion failed after forwarding", res.status);
          Office.context.ui.closeContainer();
        })
        .catch((err) => {
          console.error("Cloud report error", err);
          showError("report-email-error", "report-email-error-detail", errorMessage(err));
        });
    }
  }
}

async function setInitialContent() {
  if (!isCloud) {
    // On-prem: read internet headers via EWS first, then ask backend
    const itemId = Office.context.mailbox.item.itemId;
    ewsGetInternetHeaders(itemId)
      .then(({ headers, changeKey }) => {
        itemChangeKey = changeKey;
        const dispatch = extractDispatchId(headers);
        let endpoint: string;
        if (!dispatch) {
          endpoint = '/addin/d/none';
        } else if (dispatch.type === 'campaign') {
          endpoint = '/addin/d/' + dispatch.id;
        } else {
          endpoint = '/addin/t/' + dispatch.id;
        }
        return callBackendApi(endpoint);
      })
      .then((res) => handleDispatchResponse(res))
      .catch((error) => {
        showError("app-body-api-not-accessible", "api-error-detail", errorMessage(error));
        console.error("setInitialContent on-prem error:", error);
      });
  } else {
    callBackendApi('/addin/dispatchexistence')
      .then((res) => handleDispatchResponse(res))
      .catch(function (error) {
        showError("app-body-api-not-accessible", "api-error-detail", errorMessage(error));
        console.error(error);
      });
  }
}

async function handleDispatchResponse(response) {
  toggleDisplay("sideload-msg", "none");
  if (!response || !response.ok) {
    if (response && response.status === 404) {
      if (!isCloud) {
        // On-prem: extract secops mailbox from 404 body
        try {
          const data = await response.json();
          secopsMail = data.secopsMail ?? "";
          console.log("Real phishing detected. secops mailbox: " + secopsMail);
        } catch (e) {
          console.warn("Could not parse secopsMail from 404 response", e);
        }
      }
      toggleDisplay("app-body-real-phishing", "flex");
    } else {
      if (isCloud && graphToken && !authCode) {
        console.log("Retrying with authCode due to 500 error");
        graphToken = null;
        signIn();
      } else {
        toggleDisplay("app-body-api-not-accessible", "flex");
      }
    }
  } else {
    toggleDisplay("app-body-simulation", "flex");
  }
}

// === EWS HELPER LAYER (on-prem only) ===

const EWS_MSG_NS = "http://schemas.microsoft.com/exchange/services/2006/messages";
const EWS_TYPE_NS = "http://schemas.microsoft.com/exchange/services/2006/types";

// Escapes a string for safe interpolation into SOAP XML (subjects, addresses, ids).
function escapeXml(value: string): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// Throws with the EWS MessageText when a response message reports an error.
function throwIfEwsError(doc: Document, fallback: string): void {
  const responseClass = doc.querySelector("[ResponseClass]");
  if (responseClass && responseClass.getAttribute("ResponseClass") === "Error") {
    const msgNode = doc.getElementsByTagNameNS(EWS_MSG_NS, "MessageText")[0];
    throw new Error(msgNode && msgNode.textContent ? msgNode.textContent : fallback);
  }
}

function makeEwsRequest(soapBody: string): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.makeEwsRequestAsync(soapBody, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(result.error);
      }
    });
  });
}

function ewsGetInternetHeaders(itemId: string): Promise<{ headers: Array<{ Name: string; Value: string }>; changeKey: string }> {
  const soap = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013"/>
  </soap:Header>
  <soap:Body>
    <m:GetItem>
      <m:ItemShape>
        <t:BaseShape>IdOnly</t:BaseShape>
        <t:AdditionalProperties>
          <t:FieldURI FieldURI="item:InternetMessageHeaders"/>
        </t:AdditionalProperties>
      </m:ItemShape>
      <m:ItemIds>
        <t:ItemId Id="${escapeXml(itemId)}"/>
      </m:ItemIds>
    </m:GetItem>
  </soap:Body>
</soap:Envelope>`;

  return makeEwsRequest(soap).then((xmlResponse) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlResponse, "text/xml");
    throwIfEwsError(doc, "GetItem (headers) failed");

    const itemIdNode = doc.getElementsByTagNameNS(EWS_TYPE_NS, "ItemId")[0];
    const changeKey = itemIdNode ? (itemIdNode.getAttribute("ChangeKey") || "") : "";

    const headerNodes = doc.getElementsByTagNameNS(EWS_TYPE_NS, "InternetMessageHeader");
    const headers: Array<{ Name: string; Value: string }> = [];
    for (let i = 0; i < headerNodes.length; i++) {
      const node = headerNodes[i];
      headers.push({
        Name: node.getAttribute("HeaderName") || "",
        Value: node.textContent || ""
      });
    }
    return { headers, changeKey };
  });
}

function extractDispatchId(
  headers: Array<{ Name: string; Value: string }>
): { id: string; type: "campaign" | "testmail" } | null {
  // Mailgun sends custom variables in X-Mailgun-Variables as JSON, e.g.
  //   {"my-var": "campaignDispatchId ddd6427c-bf0b-4371-b230-24a9dd7f0027"}
  // The dispatch marker ("campaignDispatchId <id>" / "testmailDispatchId <id>")
  // is embedded inside a value string under an arbitrary key, so we scan every
  // value rather than relying on a fixed top-level key.
  const variablesHeader = headers.find(
    (h) => h.Name.toLowerCase() === "x-mailgun-variables"
  );
  if (!variablesHeader) return null;

  // Candidate strings to scan: parsed JSON values, plus the raw header as a fallback.
  const candidates: string[] = [];
  try {
    const vars = JSON.parse(variablesHeader.Value);
    if (vars && typeof vars === "object") {
      // Historic direct-key form: { "campaignDispatchId": "<id>" }
      if (vars.campaignDispatchId) return { id: String(vars.campaignDispatchId), type: "campaign" };
      if (vars.testmailDispatchId) return { id: String(vars.testmailDispatchId), type: "testmail" };
      for (const value of Object.values(vars)) {
        if (typeof value === "string") candidates.push(value);
      }
    }
  } catch (e) {
    // Not JSON — fall through to scanning the raw header value.
  }
  candidates.push(variablesHeader.Value);

  for (const text of candidates) {
    const campaignMatch = text.match(/campaignDispatchId[\s:=]+([\w-]+)/);
    if (campaignMatch) return { id: campaignMatch[1], type: "campaign" };
    const testMatch = text.match(/testmailDispatchId[\s:=]+([\w-]+)/);
    if (testMatch) return { id: testMatch[1], type: "testmail" };
  }
  return null;
}

function ewsMoveToDeletedItems(itemId: string): Promise<void> {
  const soap = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013"/>
  </soap:Header>
  <soap:Body>
    <m:MoveItem>
      <m:ToFolderId>
        <t:DistinguishedFolderId Id="deleteditems"/>
      </m:ToFolderId>
      <m:ItemIds>
        <t:ItemId Id="${escapeXml(itemId)}"/>
      </m:ItemIds>
    </m:MoveItem>
  </soap:Body>
</soap:Envelope>`;

  return makeEwsRequest(soap).then((xmlResponse) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlResponse, "text/xml");
    throwIfEwsError(doc, "MoveItem failed");
  });
}

function ewsGetMimeContent(itemId: string, changeKey: string = ""): Promise<{ mimeContent: string; subject: string; sender: string; dateTimeReceived: string }> {
  const soap = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013"/>
  </soap:Header>
  <soap:Body>
    <m:GetItem>
      <m:ItemShape>
        <t:BaseShape>AllProperties</t:BaseShape>
        <t:IncludeMimeContent>true</t:IncludeMimeContent>
      </m:ItemShape>
      <m:ItemIds>
        <t:ItemId Id="${escapeXml(itemId)}"${changeKey ? ` ChangeKey="${escapeXml(changeKey)}"` : ""}/>
      </m:ItemIds>
    </m:GetItem>
  </soap:Body>
</soap:Envelope>`;

  return makeEwsRequest(soap).then((xmlResponse) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlResponse, "text/xml");
    throwIfEwsError(doc, "GetItem (MIME) failed");
    const mimeNode = doc.getElementsByTagNameNS(EWS_TYPE_NS, "MimeContent")[0];
    const subjectNode = doc.getElementsByTagNameNS(EWS_TYPE_NS, "Subject")[0];
    const dateTimeReceivedNode = doc.getElementsByTagNameNS(EWS_TYPE_NS, "DateTimeReceived")[0];
    const fromNode = doc.getElementsByTagNameNS(EWS_TYPE_NS, "From")[0];
    const senderEmailNode = fromNode
      ? fromNode.getElementsByTagNameNS(EWS_TYPE_NS, "EmailAddress")[0]
      : null;
    return {
      mimeContent: mimeNode ? mimeNode.textContent || "" : "",
      subject: subjectNode ? subjectNode.textContent || "" : "",
      sender: senderEmailNode ? senderEmailNode.textContent || "" : "",
      dateTimeReceived: dateTimeReceivedNode ? dateTimeReceivedNode.textContent || "" : ""
    };
  });
}

function ewsForwardEmail(itemId: string, toAddress: string, changeKey: string = ""): Promise<void> {
  return ewsGetMimeContent(itemId, changeKey).then(({ mimeContent, subject, sender, dateTimeReceived }) => {
    const wrapperSubject = escapeXml("[look2x Meldung: Verdächtige E-Mail] " + subject);
    const recipient = escapeXml(toAddress);
    const bodyText = escapeXml(
      "Eine verdächtige E-Mail wurde über den look2x Phishing-Melde-Button gemeldet. Unten finden Sie weitere Informationen:\n\n" +
      "Absender: " + sender + "\n" +
      "Betreff: " + subject + "\n" +
      "Empfangsdatum: " + dateTimeReceived + "\n\n" +
      "Bitte überprüfen Sie die angehängte .eml-Datei in einer geschützten Umgebung für weitere Details und ergreifen Sie die entsprechenden Maßnahmen."
    );
    const soap = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013"/>
  </soap:Header>
  <soap:Body>
    <m:CreateItem MessageDisposition="SendAndSaveCopy">
      <m:Items>
        <t:Message>
          <t:Subject>${wrapperSubject}</t:Subject>
          <t:Body BodyType="Text">${bodyText}</t:Body>
          <t:ToRecipients>
            <t:Mailbox>
              <t:EmailAddress>${recipient}</t:EmailAddress>
            </t:Mailbox>
          </t:ToRecipients>
          <t:Attachments>
            <t:FileAttachment>
              <t:Name>original.eml</t:Name>
              <t:ContentType>message/rfc822</t:ContentType>
              <t:Content>${mimeContent}</t:Content>
            </t:FileAttachment>
          </t:Attachments>
        </t:Message>
      </m:Items>
    </m:CreateItem>
  </soap:Body>
</soap:Envelope>`;

    return makeEwsRequest(soap).then((xmlResponse) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlResponse, "text/xml");
      throwIfEwsError(doc, "CreateItem failed");
    });
  });
}

// === CENTRAL API LOGIC ===

function getTargetId() {
  if (!isCloud) {
    return Office.context.mailbox.item.itemId;
  }

  const hostName = Office.context.mailbox.diagnostics.hostName;
  if (hostName === 'OutlookIOS' || hostName === 'OutlookAndroid') {
    return Office.context.mailbox.item.itemId;
  } else {
    return Office.context.mailbox.convertToRestId(
      Office.context.mailbox.item.itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );
  }
}

function getBaseUrl() {
  let url = 'https://app.look2x.de/public';
  if (process.env.APP_ENV === 'production') url = 'https://app.look2x.de/public';
  if (process.env.APP_ENV === 'staging') url = 'https://staging.app.look2x.de/public';
  if (process.env.APP_ENV === 'development') url = 'https://5204-2003-ef-373a-6700-284e-a795-a2a4-bd1d.ngrok-free.app/public';
  return url;
}

async function callBackendApi(endpoint: string, extras: Record<string, unknown> = {}) {
  const requestBody: any = {
    isCloud: isCloud,
    ...extras,
  };

  if (!isCloud) {
    requestBody.exchangeHost = exchangeHost;
  } else {
    requestBody.targetId = getTargetId();
    if (graphToken) requestBody.accessToken = graphToken;
    if (authCode) requestBody.authCode = authCode;
  }

  try {
    const response = await fetch(getBaseUrl() + endpoint, {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });
    return response;
  } catch (error) {
    console.error(`Error calling ${endpoint}: `, error);
    throw error;
  }
}
